import type {
  McpAdminIntegrationListItem,
  McpDiscoveryResult,
  McpIntegrationImpact,
  McpIntegrationTestResult,
  McpOauthCallbackInfo,
  McpOauthDiscoveryView,
} from "@yazamco/shared-types";
import type {
  McpIntegrationCreateInput,
  McpIntegrationUpdateInput,
} from "@yazamco/validation";
import { CopilotApiError } from "./copilot";

/**
 * The GLOBAL MCP catalog, from the browser's side.
 *
 * A separate module from `copilot.ts` for the same reason the routes are a
 * separate file: everything there is one person acting on their OWN connection
 * to a business system somebody else approved, and everything here decides —
 * for everybody — which business systems exist and what a language model may be
 * shown from them. Two audiences, two permissions, two modules.
 *
 * ── What this module cannot receive ─────────────────────────────────────────
 *
 * A secret. Not the OAuth client secret, not a shared service-account
 * credential, not ciphertext, not a fragment. The response types have no field
 * for one — `secrets` is two booleans — and the API builds its auth view field
 * by field rather than by spreading the stored configuration. The browser is
 * therefore incapable of displaying, comparing or reconstructing a stored
 * credential, which is what makes the "Configured ✓ / Replace" control honest
 * rather than a UI convention.
 *
 * A secret travels in exactly one direction: typed into the wizard, sent once,
 * and never seen again.
 */

async function request<T>(
  method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  const response = await fetch(`/api/v1${path}`, {
    method,
    headers: body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const payload = (await response.json().catch(() => ({}))) as T & {
    error?: { code?: string; message?: string; details?: unknown };
  };
  if (!response.ok) {
    throw new CopilotApiError(
      payload.error?.message ?? `Request failed (${response.status}).`,
      payload.error?.code ?? "COPILOT_MCP_ADMIN_FAILED",
      response.status,
      payload.error?.details,
    );
  }
  return payload;
}

export const fetchMcpAdminIntegrations = (): Promise<{
  items: McpAdminIntegrationListItem[];
}> => request("GET", "/copilot/mcp/admin/integrations");

export const fetchMcpAdminIntegration = (
  id: string,
): Promise<{ integration: McpAdminIntegrationListItem }> =>
  request("GET", `/copilot/mcp/admin/integrations/${encodeURIComponent(id)}`);

/**
 * The callback URL an administrator registers with their OAuth provider.
 *
 * Fetched rather than assembled in the browser from `window.location`. The
 * value has to match what the API will actually send as `redirect_uri`, and
 * that comes from the deployment's trusted public-origin configuration — a URL
 * built from the page's own address would be right on most deployments and
 * silently wrong behind a proxy, producing an OAuth failure whose message names
 * a value the administrator cannot see.
 */
export const fetchMcpOauthCallbackUrl = (): Promise<McpOauthCallbackInfo> =>
  request("GET", "/copilot/mcp/admin/oauth-callback-url");

/**
 * Ask the API to read a provider's own OAuth settings off its MCP endpoint.
 *
 * A POST with the endpoint in the BODY, although nothing is written. A URL in a
 * query string ends up in this deployment's access log and in every proxy's
 * between here and it, and the whole point of the button is that an
 * administrator pastes URLs into it while working out which one is right.
 *
 * What comes back contains no client ID, because obtaining one registers an
 * OAuth application in the provider's console — a write that happens once, when
 * the integration is saved, not every time somebody presses Detect.
 */
export const discoverMcpOauth = (
  endpoint: string,
  clientName?: string,
): Promise<{ discovery: McpOauthDiscoveryView }> =>
  request("POST", "/copilot/mcp/admin/oauth-discovery", {
    endpoint,
    ...(clientName ? { clientName } : {}),
  });

export const createMcpIntegration = (
  input: McpIntegrationCreateInput,
): Promise<{ integration: McpAdminIntegrationListItem }> =>
  request("POST", "/copilot/mcp/admin/integrations", input);

export const updateMcpIntegration = (
  id: string,
  input: McpIntegrationUpdateInput,
): Promise<{ integration: McpAdminIntegrationListItem }> =>
  request("PATCH", `/copilot/mcp/admin/integrations/${encodeURIComponent(id)}`, input);

/**
 * Delete, with the slug typed back.
 *
 * The confirmation reaches the API in the body, not merely the dialog: a dialog
 * is an affordance and can be clicked through, and the failure being guarded is
 * an administrator with two tabs open deleting the wrong integration and taking
 * a dozen people's connections with it.
 */
export const deleteMcpIntegration = (
  id: string,
  confirmSlug: string,
): Promise<{ ok: true; impact: McpIntegrationImpact }> =>
  request("DELETE", `/copilot/mcp/admin/integrations/${encodeURIComponent(id)}`, {
    confirmSlug,
  });

export const testMcpIntegration = (
  id: string,
): Promise<{ result: McpIntegrationTestResult }> =>
  request("POST", `/copilot/mcp/admin/integrations/${encodeURIComponent(id)}/test`);

export const discoverMcpTools = (id: string): Promise<McpDiscoveryResult> =>
  request(
    "POST",
    `/copilot/mcp/admin/integrations/${encodeURIComponent(id)}/discover-tools`,
  );

export const setMcpAllowedTools = (
  id: string,
  tools: string[],
  version: number,
): Promise<{ integration: McpAdminIntegrationListItem }> =>
  request(
    "PUT",
    `/copilot/mcp/admin/integrations/${encodeURIComponent(id)}/allowed-tools?version=${version}`,
    { tools },
  );
